import React, { useEffect, useState, useCallback } from 'react';
import { Radar, Monitor, Printer, Network, Smartphone, HelpCircle, Cpu, RefreshCw, EyeOff, PackagePlus, X, MapPin, Search, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

const TYPE_META = {
  pc:      { label: 'PC / Servidor',  icon: Monitor,    color: 'var(--accent)' },
  printer: { label: 'Impresora',      icon: Printer,    color: 'var(--amber)' },
  network: { label: 'Equipo de red',  icon: Network,    color: 'var(--ink-2)' },
  phone:   { label: 'Teléfono',       icon: Smartphone, color: '#0d9488' },
  iot:     { label: 'IoT / Otro',     icon: Cpu,        color: 'var(--ink-2)' },
  unknown: { label: 'Sin clasificar', icon: HelpCircle, color: 'var(--ink-3)' },
};
const CONF = { alta: 'var(--green)', media: 'var(--amber)', baja: 'var(--ink-3)' };

const ASSET_TYPES = [
  { v: 'physical',  label: 'Físico' },
  { v: 'virtual',   label: 'Virtual' },
  { v: 'cloud',     label: 'Cloud' },
  { v: 'container', label: 'Contenedor' },
];
const DEVICE_SUBTYPES = [
  { v: 'pc',      label: 'PC / Servidor' },
  { v: 'printer', label: 'Impresora' },
  { v: 'network', label: 'Equipo de red' },
  { v: 'phone',   label: 'Teléfono' },
  { v: 'iot',     label: 'IoT / Otro' },
  { v: 'other',   label: 'Otro' },
];
const inp = { width: '100%', padding: '7px 9px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border, var(--border-2))', background: 'var(--surface, #fff)', color: 'var(--ink, var(--ink))', boxSizing: 'border-box' };
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 11 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3, var(--ink-2))', marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

// ── Modal: incorporar candidato al inventario ────────────────────────────────
function IncorporateModal({ device, onClose, onDone }) {
  const [add, setAdd] = useState(true);
  const [name, setName] = useState(device.hostname || '');
  const [assetType, setAssetType] = useState('physical');
  const [subtype, setSubtype] = useState(device.device_type && device.device_type !== 'unknown' ? device.device_type : 'other');
  const [location, setLocation] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setSaving(true); setErr('');
    try {
      const r = await fetch(`${API_URL}/discovery/candidates/${device.id}/incorporate`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          asset_type: assetType, device_subtype: subtype,
          name: name.trim() || undefined, location_name: location.trim() || undefined,
        }),
      });
      const d = await r.json();
      if (!r.ok) { setErr(d.error || 'Error incorporando.'); setSaving(false); return; }
      onDone();
    } catch { setErr('Error de red.'); setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()} className="card" style={{ width: 420, maxWidth: '92vw', padding: 0, background: 'var(--surface, #fff)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border, var(--border))', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink, var(--ink))' }}>Incorporar a inventario</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3, var(--ink-3))' }}><X size={18} /></button>
        </div>
        <div style={{ padding: 18 }}>
          <div style={{ fontSize: 12, color: 'var(--ink-3, var(--ink-2))', marginBottom: 14 }}>
            {device.vendor || 'Fabricante desconocido'} · {fmtMac(device.mac)} · {device.ip || 's/IP'}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: 'var(--ink, var(--ink))', marginBottom: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={add} onChange={e => setAdd(e.target.checked)} /> Agregar al inventario
          </label>
          <div style={{ opacity: add ? 1 : 0.45, pointerEvents: add ? 'auto' : 'none' }}>
            <Field label="Nombre"><input value={name} onChange={e => setName(e.target.value)} placeholder="(se genera si lo dejás vacío)" style={inp} /></Field>
            <Field label="Tipo de activo">
              <select value={assetType} onChange={e => setAssetType(e.target.value)} style={inp}>
                {ASSET_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Categoría">
              <select value={subtype} onChange={e => setSubtype(e.target.value)} style={inp}>
                {DEVICE_SUBTYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Ubicación (opcional)"><input value={location} onChange={e => setLocation(e.target.value)} placeholder="Ej: Oficina central" style={inp} /></Field>
          </div>
          {err && <div style={{ color: 'var(--red, var(--red))', fontSize: 12, marginTop: 8 }}>{err}</div>}
        </div>
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border, var(--border))', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} className="btn btn-secondary">Cancelar</button>
          <button onClick={submit} disabled={!add || saving} className="btn btn-primary">{saving ? 'Agregando…' : 'Agregar al inventario'}</button>
        </div>
      </div>
    </div>
  );
}

function fmtMac(m) {
  const h = String(m || '').replace(/[^0-9a-fA-F]/g, '').toUpperCase();
  return h.length === 12 ? h.match(/.{2}/g).join(':') : m;
}
function fmtWhen(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// ── Designación de escáneres ────────────────────────────────────────────────
function ScannerManager() {
  const [assets, setAssets] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(null);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    fetch(`${API_URL}/discovery/scanners`, { headers: authHeaders() })
      .then(r => r.json()).then(d => setAssets(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (a) => {
    setSaving(a.id);
    try {
      await fetch(`${API_URL}/discovery/scanners/${a.id}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ enabled: !a.network_scanner }),
      });
      load();
    } catch { /* noop */ } finally { setSaving(null); }
  };

  const scanNow = async (a) => {
    setMsg('');
    try {
      const r = await fetch(`${API_URL}/commands`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ asset_id: a.id, command_type: 'run_discovery', payload: {}, timeout_sec: 120 }),
      });
      const d = await r.json();
      setMsg(d.id
        ? `✓ Escaneo encolado en ${a.hostname} — corre en ≤15 min (requiere agente ≥ v1.12.1).`
        : (d.error || 'No se pudo encolar el comando.'));
    } catch { setMsg('Error encolando el comando.'); }
  };

  const scanners = assets.filter(a => a.network_scanner);

  return (
    <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--ink, var(--ink))' }}>Equipos escáner</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3, var(--ink-2))' }}>
            {scanners.length === 0
              ? 'Ningún equipo designado — el descubrimiento no corre hasta designar al menos uno.'
              : `${scanners.length} designado(s): ${scanners.map(s => s.hostname).join(', ')}`}
          </div>
        </div>
        <span style={{ fontSize: 12, color: 'var(--blue, #1B7FF9)' }}>{open ? 'Ocultar' : 'Administrar'}</span>
      </div>
      {open && (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--border, var(--border))', paddingTop: 12 }}>
          <p style={{ fontSize: 12, color: 'var(--ink-3, var(--ink-2))', margin: '0 0 10px' }}>
            Designá 1 (máx. 2) equipos por subred. Solo esos escanean, 1 vez al día, usando la tabla ARP (suave).
          </p>
          {msg && <div style={{ fontSize: 12, color: 'var(--ink-2, var(--ink-2))', background: 'var(--gray-100, var(--bg))', borderRadius: 6, padding: '6px 10px', marginBottom: 8 }}>{msg}</div>}
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {assets.map(a => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 4px', borderBottom: '1px solid var(--gray-100, var(--bg))' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', flex: 1, minWidth: 0 }}>
                  <input type="checkbox" checked={!!a.network_scanner} disabled={saving === a.id} onChange={() => toggle(a)} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink, var(--ink))', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.hostname}</span>
                </label>
                {a.network_scanner && (
                  <button onClick={() => scanNow(a)} title="Ejecutar el sonar ahora (sin esperar el ciclo diario)"
                    style={{ fontSize: 11, padding: '3px 10px', border: '1px solid var(--border, var(--border-2))', borderRadius: 6, background: 'var(--surface, #fff)', color: 'var(--blue, #1B7FF9)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                    <Radar size={12} /> Escanear ahora
                  </button>
                )}
                <span style={{ fontSize: 11, color: 'var(--ink-3, var(--ink-3))', minWidth: 120, textAlign: 'right', flexShrink: 0 }}>
                  últ. {fmtWhen(a.last_seen)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Modal: confirmar análisis profundo ──────────────────────────────────────
function DeepScanModal({ items, onClose, onConfirm }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div onClick={e => e.stopPropagation()} className="card" style={{ width: 460, maxWidth: '92vw', padding: 0, background: 'var(--surface, #fff)' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border, var(--border))', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--ink, var(--ink))' }}>Analizar en profundidad</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3, var(--ink-3))' }}><X size={18} /></button>
        </div>
        <div style={{ padding: 18 }}>
          <p style={{ fontSize: 13, color: 'var(--ink-2, var(--ink-2))', margin: '0 0 10px' }}>
            Se sondearán activamente <b>{items.length}</b> equipo(s) desde su escáner (puertos por rol, título HTTP y SSDP), solo a las IPs listadas. Corre en ≤15 min y requiere agente ≥ v1.13.0.
          </p>
          <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border, var(--border))', borderRadius: 6 }}>
            {items.map(d => (
              <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 10px', borderBottom: '1px solid var(--gray-100, var(--bg))', fontSize: 12 }}>
                <span style={{ fontFamily: 'monospace', color: 'var(--ink-2, var(--ink-2))' }}>{d.ip}</span>
                <span style={{ color: 'var(--ink-3, var(--ink-2))', flex: 1, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.vendor || fmtMac(d.mac)}</span>
                <span style={{ color: 'var(--ink-3, var(--ink-3))' }}>{d.site}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border, var(--border))', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} className="btn btn-secondary">Cancelar</button>
          <button onClick={onConfirm} className="btn btn-primary">Analizar ({items.length})</button>
        </div>
      </div>
    </div>
  );
}

// ── Página principal ────────────────────────────────────────────────────────
export default function NetworkDiscovery() {
  const [devices, setDevices] = useState([]);
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fType, setFType] = useState('');
  const [showIgnored, setShowIgnored] = useState(false);
  const [incDev, setIncDev] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [deepOpen, setDeepOpen] = useState(false);
  const [deepMsg, setDeepMsg] = useState('');
  const [clearing, setClearing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (fType) qs.set('type', fType);
    if (showIgnored) qs.set('status', 'ignorado');
    fetch(`${API_URL}/discovery/candidates?${qs}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setDevices(d.devices || []); setCounts(d.counts || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fType, showIgnored]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await fetch(`${API_URL}/discovery/candidates/${id}`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ status }),
      });
      load();
    } catch { /* noop */ }
  };

  const setType = async (id, device_type) => {
    try {
      await fetch(`${API_URL}/discovery/candidates/${id}`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ device_type }),
      });
      load();
    } catch { /* noop */ }
  };

  const toggleSel = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const clearPrevious = async () => {
    if (!window.confirm('¿Borrar las búsquedas anteriores?\n\nSe eliminan los equipos descubiertos que NO fueron incorporados al inventario (nuevos, revisados e ignorados). Los ya incorporados se conservan. En el próximo escaneo se vuelven a listar los que sigan presentes en la red.')) return;
    setClearing(true);
    try {
      const r = await fetch(`${API_URL}/discovery/candidates`, { method: 'DELETE', headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) { alert(d.error || 'No se pudo borrar.'); return; }
      setSelected(new Set());
      load();
    } catch { alert('Error de red.'); } finally { setClearing(false); }
  };

  const runDeepScan = async () => {
    setDeepMsg('');
    try {
      const r = await fetch(`${API_URL}/discovery/deep-scan`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ ids: Array.from(selected) }),
      });
      const d = await r.json();
      if (!r.ok) { setDeepMsg(d.error || 'No se pudo encolar.'); setDeepOpen(false); return; }
      setDeepMsg(`✓ Análisis encolado en ${d.enqueued} escáner(es) — corre en ≤15 min (requiere agente ≥ v1.13.0).`);
      setDeepOpen(false); setSelected(new Set());
    } catch { setDeepMsg('Error de red.'); setDeepOpen(false); }
  };

  // Agrupar por sitio del escáner que los descubrió
  const groups = {};
  for (const d of devices) { const s = d.site || 'Sin ubicación'; (groups[s] = groups[s] || []).push(d); }
  const siteNames = Object.keys(groups).sort((a, b) => (a === 'Sin ubicación') - (b === 'Sin ubicación') || a.localeCompare(b));
  const selCount = selected.size;

  const countBy = (s) => (counts.find(c => c.status === s)?.n) || 0;
  const nuevos = countBy('nuevo');

  return (
    <div style={{ maxWidth: 1150, margin: '0 auto', padding: '28px 24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Radar size={22} style={{ color: 'var(--ink-2)' }} />
            <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink, var(--ink))', margin: 0 }}>Descubrimiento de red</h1>
            {nuevos > 0 && (
              <span style={{ color: 'var(--red, var(--red))', fontSize: 12, fontWeight: 700 }}>
                {nuevos} nuevo{nuevos > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-3, var(--ink-3))' }}>
            Equipos detectados en la red que <b>no tienen agente</b> — candidatos a incorporar.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button onClick={clearPrevious} disabled={clearing} className="btn btn-secondary" title="Borra los candidatos no incorporados; en el próximo escaneo se re-listan los presentes"
            style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--red, #c0392b)' }}>
            <Trash2 size={14} /> {clearing ? 'Borrando…' : 'Borrar búsquedas anteriores'}
          </button>
          <button onClick={load} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={14} /> Actualizar
          </button>
        </div>
      </div>

      <ScannerManager />

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {['', 'pc', 'printer', 'network', 'phone', 'iot', 'unknown'].map(t => (
          <button key={t || 'all'} onClick={() => setFType(t)}
            style={{ padding: '4px 12px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border, var(--border-2))', cursor: 'pointer',
              background: fType === t ? 'var(--blue, #1B7FF9)' : 'var(--surface, #fff)',
              color: fType === t ? '#fff' : 'var(--ink-2, var(--ink-2))', fontWeight: fType === t ? 700 : 400 }}>
            {t ? (TYPE_META[t]?.label || t) : 'Todos'}
          </button>
        ))}
        <label style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink-3, var(--ink-2))', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={showIgnored} onChange={e => setShowIgnored(e.target.checked)} /> Ver ignorados
        </label>
      </div>

      {/* Barra de acción para análisis profundo */}
      {(selCount > 0 || deepMsg) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
          {selCount > 0 && (
            <button onClick={() => setDeepOpen(true)} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Search size={14} /> Analizar en profundidad ({selCount})
            </button>
          )}
          {selCount > 0 && <button onClick={() => setSelected(new Set())} className="btn btn-secondary">Limpiar</button>}
          {deepMsg && <span style={{ fontSize: 12, color: 'var(--ink-2, var(--ink-2))', background: 'var(--gray-100, var(--bg))', borderRadius: 6, padding: '6px 10px' }}>{deepMsg}</span>}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 50, color: 'var(--ink-3)' }}>
          <div className="spinner" style={{ margin: '0 auto 10px', width: 26, height: 26 }} /> Cargando…
        </div>
      ) : devices.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '56px 20px', color: 'var(--ink-3, var(--ink-3))' }}>
          <Radar size={44} style={{ margin: '0 auto 14px', display: 'block', opacity: 0.3 }} />
          <div style={{ fontSize: 15, fontWeight: 600 }}>Sin candidatos todavía</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Designá un equipo escáner arriba; el descubrimiento corre 1 vez al día.</div>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 28 }}></th>
                  <th>Tipo</th><th>Nombre</th><th>IP</th><th>Fabricante</th>
                  <th>MAC</th><th>Subred</th><th>Confianza</th><th>Visto</th><th style={{ textAlign: 'right' }}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {siteNames.map(site => (
                  <React.Fragment key={site}>
                    <tr>
                      <td colSpan={10} style={{ background: 'var(--gray-100, var(--bg))', fontWeight: 700, fontSize: 12, color: 'var(--ink-2, var(--ink-2))', padding: '6px 12px' }}>
                        <MapPin size={12} style={{ verticalAlign: '-2px', marginRight: 6, color: 'var(--ink-2)' }} />
                        {site} <span style={{ color: 'var(--ink-3, var(--ink-3))', fontWeight: 400 }}>({groups[site].length})</span>
                      </td>
                    </tr>
                    {groups[site].map(d => {
                      const meta = TYPE_META[d.device_type] || TYPE_META.unknown;
                      const Icon = meta.icon;
                      return (
                        <tr key={d.id}>
                          <td style={{ textAlign: 'center' }}>
                            <input type="checkbox" checked={selected.has(d.id)}
                              disabled={!d.ip}
                              title={d.ip ? 'Seleccionar para análisis profundo' : 'Sin IP: no se puede sondear'}
                              onChange={() => toggleSel(d.id)} />
                          </td>
                          <td>
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <Icon size={15} style={{ color: meta.color, flexShrink: 0 }} />
                              <select value={d.device_type || 'unknown'} onChange={e => setType(d.id, e.target.value)} title="Cambiar tipo"
                                style={{ fontSize: 12, fontWeight: 600, color: meta.color, background: 'transparent', border: '1px solid var(--border, var(--border))', borderRadius: 6, padding: '2px 4px', cursor: 'pointer' }}>
                                {['pc', 'printer', 'network', 'phone', 'iot', 'unknown'].map(t => (
                                  <option key={t} value={t} style={{ color: 'var(--ink, var(--ink))' }}>{TYPE_META[t].label}</option>
                                ))}
                              </select>
                              {d.classified_by === 'ia' && <span title="Clasificado por IA" style={{ fontSize: 10 }}>🤖</span>}
                              {d.classified_by === 'manual' && <span title="Tipo asignado a mano" style={{ fontSize: 10 }}>✏️</span>}
                            </div>
                          </td>
                          <td style={{ fontSize: 13 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <span>
                                {d.status === 'nuevo' && <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: 'var(--green)', marginRight: 6 }} title="Nuevo" />}
                                {d.hostname || d.model || <span style={{ color: 'var(--ink-3)' }}>(sin nombre)</span>}
                              </span>
                              {d.deep_scanned_at && (
                                <span style={{ fontSize: 11, color: 'var(--ink-3, var(--ink-3))', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  <Search size={10} style={{ flexShrink: 0 }} />
                                  {d.hostname && d.model ? d.model : ''}
                                  {d.hostname && d.model && d.open_ports && d.open_ports.length ? ' · ' : ''}
                                  {d.open_ports && d.open_ports.length ? `puertos ${d.open_ports.join(', ')}` : (d.hostname && d.model ? '' : 'sin puertos abiertos')}
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--ink-2, var(--ink-2))' }}>{d.ip || '—'}</td>
                          <td style={{ fontSize: 12 }}>{d.vendor || '—'}</td>
                          <td style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--ink-3, var(--ink-2))' }}>{fmtMac(d.mac)}</td>
                          <td style={{ fontSize: 11, color: 'var(--ink-3, var(--ink-2))' }}>{d.subnet || '—'}</td>
                          <td><span style={{ fontSize: 11, fontWeight: 600, color: CONF[d.confidence] || 'var(--ink-3)' }}>{d.confidence}</span></td>
                          <td style={{ fontSize: 11, color: 'var(--ink-3, var(--ink-2))' }}>{fmtWhen(d.last_seen)}</td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {d.status !== 'incorporado' && (
                              <button onClick={() => setIncDev(d)} title="Incorporar a inventario"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--green)', padding: 4 }}>
                                <PackagePlus size={16} />
                              </button>
                            )}
                            {d.status !== 'ignorado'
                              ? <button onClick={() => setStatus(d.id, 'ignorado')} title="Ignorar"
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-3)', padding: 4 }}>
                                  <EyeOff size={16} />
                                </button>
                              : <button onClick={() => setStatus(d.id, 'nuevo')} title="Restaurar"
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', padding: 4, fontSize: 11 }}>
                                  restaurar
                                </button>}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {incDev && <IncorporateModal device={incDev} onClose={() => setIncDev(null)} onDone={() => { setIncDev(null); load(); }} />}
      {deepOpen && <DeepScanModal items={devices.filter(d => selected.has(d.id))} onClose={() => setDeepOpen(false)} onConfirm={runDeepScan} />}
    </div>
  );
}
